import { type IGetActionsData, getAxiosErrorMessage, useGetActionById, usePutAction } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { ACTION_IMAGE_LINK_ATTRIBUTE } from "@data-display/ActionTextContent";
import { yupResolver } from "@hookform/resolvers/yup";
import { useBatchCreateActions } from "@root/api/services/action/batchCreateActions";
import { useGlobalContext } from "@root/context";
import { skipWeekend } from "@root/utils/date/skipWeekend";
import { useI18nContext } from "@root/utils/organisation";
import { isCancel } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { IModalActionOptions } from "..";
import { baseSchema } from "../resolvers";
import { formatActionToFormDefaultValues, formatFormDataToAction } from "./format";
import type { IModalActionForm, IModalActionResolver, TModalAction } from "./types";
import { buildSuccessMessage } from "./utils";

const hasLightenedComments = (action: TModalAction) => Boolean(action.comments?.includes(ACTION_IMAGE_LINK_ATTRIBUTE));

export const useModalAction = (
  open: boolean,
  action?: TModalAction,
  onSubmit?: (action: IGetActionsData) => void,
  overrideSubmit?: (action: IModalActionResolver) => void | Promise<void>,
  options?: IModalActionOptions
) => {
  const { user, settingsV2, challenges } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const [loading, setLoading] = useState(false);
  const openedActionIdRef = useRef<ObjectId | null>(null);
  const [, createAction] = useBatchCreateActions();
  const [, updateAction] = usePutAction();
  const [, getAction] = useGetActionById();
  const resolver = useMemo(
    () =>
      yupResolver(
        baseSchema(
          user.organisation.features.action.requiredContactDetails,
          options?.hideCandidateField,
          options?.hideApproachField
        )
      ),
    [user]
  );
  const { control, handleSubmit, reset, setValue, formState, watch } = useForm<
    IModalActionForm,
    void,
    IModalActionResolver
  >({
    // biome-ignore lint/suspicious/noExplicitAny: react-hook-form issue with resolver's type
    resolver: resolver as any,
  });

  const submit = async (data: IModalActionResolver) => {
    setLoading(true);
    try {
      if (overrideSubmit) {
        await overrideSubmit(data);
      }
      if (onSubmit) {
        const formattedAction = formatFormDataToAction(data, timeZone);
        if (action?._id) {
          const clearEndDate = !data.type.metadata?.approachActionType && !data.endTime;
          const response = await updateAction(action._id, {
            ...formattedAction,
            ...(clearEndDate && { endDate: null }),
          });
          window.publish("feedback", {
            severity: "success",
            message: "L'action a bien été modifiée",
          });
          onSubmit(response.data);
        } else {
          const response = await createAction([formattedAction]);
          const action = await getAction(response.data[0]);
          window.publish("feedback", {
            severity: "success",
            message: buildSuccessMessage(data, user, timeZone, formattedAction.type),
          });
          onSubmit(action.data);
        }

        if (data.jobFollowUp?.nextFollowUpDate) {
          await createAction([
            {
              ...formattedAction,
              comments: undefined,
              executionDate: undefined,
              deadlineDate: skipWeekend(data.jobFollowUp.nextFollowUpDate),
              jobSituation: undefined,
              status: "todo",
              isAutoGenerate: true,
            },
          ]);
        }
      }
    } catch (error) {
      window.publish("feedback", {
        severity: "error",
        message:
          getAxiosErrorMessage(error) ??
          "Une erreur est survenue lors de l'enregistrement de cette action. Veuillez contacter un administrateur.",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = (defaultAction: TModalAction | undefined) => {
    reset(
      formatActionToFormDefaultValues(
        defaultAction,
        settingsV2.statusTodoType,
        settingsV2.actionChannel,
        challenges,
        user,
        timeZone
      )
    );
  };

  const loadFullComments = async (actionId: ObjectId) => {
    setLoading(true);
    try {
      const response = await getAction(actionId);
      if (openedActionIdRef.current !== actionId) {
        return;
      }
      setValue("comments", response.data.comments ?? "");
      setLoading(false);
    } catch (error) {
      if (isCancel(error) || openedActionIdRef.current !== actionId) {
        return;
      }
      window.publish("feedback", {
        severity: "error",
        message: "L'action n'a pas pu être chargée. Veuillez fermer la fenêtre et réessayer.",
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: only update on open
  useEffect(() => {
    if (!open) {
      openedActionIdRef.current = null;
      return;
    }
    openedActionIdRef.current = action?._id ?? null;
    setLoading(false);
    if (action?._id && hasLightenedComments(action)) {
      resetForm({ ...action, comments: "" });
      loadFullComments(action._id);
    } else {
      resetForm(action);
    }
  }, [open]);

  return {
    control,
    formState,
    loading,
    handleSubmit: handleSubmit(submit, (error) => {
      console.error(error);
    }),
    setValue,
    watch,
  };
};
